<!-- eslint-disable max-len -->
<template>
  <div class="ipl-campaign" v-if="pageState.status.isLoaded && page">
    <!--Hero Section -->
    <section class="hero-section">
      <div class="container">
        <div class="hero-content">
          <div class="hero-text">
            <p class="hero-subtitle">IPL 2026 Data Coverage by CricketAPI.com</p>
            <h1 class="hero-title">Get Live IPL 2026 Data</h1>
            <p class="hero-description">
              Power your fantasy app, sports website or cricket dashboard with real-time ball-by-ball IPL data.
            </p>
            <div class="cta-buttons">
              <a href="https://cal.com/ashiey-joe-pfn0im" aria-label="Book a Free 15-Min Demo"
                target="_blank" rel="noopener" class="btn-primary">
                Book a Free 15-Min Demo
                <svg width="6" height="12" viewBox="0 0 6 12" fill="none">
                  <path d="M1 1L5 6L1 11" stroke="currentColor"
                  stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </a>
              <a href="https://www.cricketapi.com/v5/user/project/review/?package=rs_package_a-rz--cricket--bcci--iplt20--2026-ZGwl_424&step=ReviewPlan" aria-label="Get API Access"
                class="btn-secondary">
                Get API Access
                <svg width="6" height="12" viewBox="0 0 6 12" fill="none">
                  <path d="M1 1L5 6L1 11" stroke="currentColor"
                  stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </a>
            </div>
          </div>
          <div class="hero-image">
            <div class="illustration-container">
              <img src="@assets/campaign/artwork-1.png" alt="Hero Banner Illustration" />
            </div>
          </div>
        </div>
        <div class="marquee">
          <div class="marquee-content">
            <ul class="list-inline">
              <template v-for="i in [1, 2, 3, 4, 5]">
                <li v-for="n in [1, 2, 3, 4, 5]" :key="`${i}-${n}`">
                  <img  :src="getClientLogoPath(n)"
                    alt="Client Logo" />
                </li>
              </template>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Client Testimonials Section -->
    <section class="testimonials-section">
      <div class="container">
        <h2 class="section-title">
          <span class="highlight-pink">Clients</span> Who Get It, Love It
        </h2>
        <div class="testimonials-grid">
          <div class="testimonial-card" v-for="testimonial in testimonials" :key="testimonial.name">
            <div class="testimonial-header">
              <div class="logo-circle"
                :style="{ background: testimonial.logoBg }"
                :class="{ bordered: testimonial.hasBorder }">
                <img :src="testimonial.logo" :alt="testimonial.name"
                  :style="testimonial.logoImgStyle" />
              </div>
              <div class="client-info">
                <span class="client-name">{{ testimonial.name }}</span>
                <span class="client-subtitle">{{ testimonial.subtitle }}</span>
              </div>
            </div>
            <div class="testimonial-divider"></div>
            <p class="testimonial-quote">{{ testimonial.quote }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Tournament Overview Section -->
    <section class="tournament-overview">
      <div class="container">
        <div class="overview-content">
          <div class="overview-text">
            <h2 class="section-title"><span class="highlight-yellow">IPL 2026</span><br />Tournament<br />Overview</h2>
            <p class="section-description">
              The Indian Premier League 2026 is one of the world's most watched T20 cricket leagues, featuring:
            </p>
            <div class="stats-grid">
              <div class="stat-item"
                v-for="(statInfo, i, index) in stats"
                :key="i" :class="{'stat-wide': (index+1)%2 === 0}">
                <div class="stat-number">{{ i }}</div>
                <div class="stat-label">{{ statInfo.label }}</div>
              </div>
            </div>
          </div>
          <div class="overview-image">
            <img src="@assets/campaign/artwork-2.png" alt="Tournament overview illustration" />
          </div>
        </div>
      </div>
    </section>

    <!-- What You Can Build Section -->
    <section class="build-section">
      <div class="container">
        <div class="build-content">
          <div class="build-text">
            <h2 class="section-title">
              What you can <span class="highlight-green">build </span>with IPL 2026 data
            </h2>
            <p class="section-subtitle">Use CricketAPI to build:</p>
            <div class="features-list">
              <div class="feature" v-for="useCase in useCases" :key="useCase">
                <span class="plus">
                  <svg><use v-bind="{'xlink:href':'#star-icon'}"></use></svg>
                </span>
                <span>{{ useCase }}</span>
              </div>
            </div>
          </div>
          <div class="image-container">
            <img src="@assets/campaign/artwork-3.png" alt="What You Can Build Illustration"/>
          </div>
        </div>
      </div>
    </section>

    <!-- Why CricketAPI Section -->
    <section class="why-section">
      <div class="container">
        <div class="why-content">
          <div class="why-text">
            <h2 class="section-title">
              <span class="highlight-yellow">Why</span> CricketAPI is Ideal for IPL
            </h2>
          </div>
          <div class="why-features">
            <div class="why-feature-item" v-for="feature in features" :key="feature">
              <svg><use v-bind="{'xlink:href':'#yellow-star-icon'}"></use></svg>
              <span v-if="feature.highlightFirst"><strong>{{ feature.highlight }}</strong> {{ feature.content }}</span>
              <span v-else>{{ feature.content }} <strong>{{ feature.highlight }}</strong></span>
            </div>
          </div>
          <div class="why-image">
            <img src="@assets/campaign/artwork-4.png" alt="Why CricketAPI Illustration"/>
          </div>
        </div>
      </div>
    </section>

    <!-- What You Get Section -->
    <section class="get-section">
      <div class="container">
        <div class="get-content">
          <div class="get-text">
            <h2 class="section-title">What You Get with IPL API Access</h2>
            <div class="api-features">
              <div class="api-feature" v-for="content in contents" :key="content">
                <svg><use v-bind="{'xlink:href':'#check-mark-icon'}"></use></svg>
                <span>{{ content }}</span>
              </div>
            </div>
          </div>
          <div class="image-container">
            <img src="@assets/campaign/artwork-5.png" alt="What You Get Illustration"/>
          </div>
        </div>
      </div>
    </section>

    <!-- Access APIs Section -->
    <section class="access-apis-section">
      <div class="container">
        <div class="apis-content">
          <h2 class="section-title">
            <span class="highlight-yellow">Access a wide</span> range of APIs
          </h2>
          <div class="api-categories">
            <div class="api-column"
              v-for="(column, ci) in apiColumns" :key="ci">
              <div class="api-category"
                v-for="category in column" :key="category.title">
                <h3>{{ category.title }}</h3>
                <ul>
                  <li v-for="item in category.items" :key="item.name">
                    <a :href="item.link">{{ item.name }}</a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div class="api-illustration">
            <img src="@assets/campaign/artwork-6.png" alt="API Illustration"/>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section 1 -->
    <div class="final-cta-container">
      <section class="final-cta-section-1">
        <div class="container">
          <p class="cta-subtitle">Get Ready Before Season Begins</p>
          <h2 class="cta-title">
            Launch Faster With <span class="highlight-yellow">CricketAPI's IPL 2026</span> Data Package
          </h2>
        </div>
      </section>

      <!-- CTA Section 2-->
      <section class="final-cta-section-2">
        <div class="container">
          <a class="btn-cta-primary" href="https://cal.com/ashiey-joe-pfn0im" aria-label="Book IPL 2026 Strategy call">
            Book a Free 15-Min Demo
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M2 2L10 6L2 10" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </a>
          <img src="@assets/campaign/artwork-7.png" />
        </div>
      </section>
    </div>
  </div>
  <div class="content placeholder" v-else>
    <CricketLoadingPlaceholder :state="pageState"/>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import CricketLoadingPlaceholder from '@/components/SportsHome/CricketLoadingPlaceholder.vue';
import clientLogo1 from '@/assets/campaign/client_logo_1.svg';
import clientLogo2 from '@/assets/campaign/client_logo_2.svg';
import clientLogo3 from '@/assets/campaign/client_logo_3.svg';
import clientLogo4 from '@/assets/campaign/client_logo_4.svg';
import clientLogo5 from '@/assets/campaign/client_logo_5.svg';
import ascoresLogo from '@/assets/campaign/testimonials/ascores.png';
import ad11Logo from '@/assets/campaign/testimonials/ad11_inner.png';
import fantomplayLogo from '@/assets/campaign/testimonials/fantomplay.svg';
import pagalfanLogo from '@/assets/campaign/testimonials/pagalfan.png';


export default {
  name: 'IPLCampaign',
  components: {
    CricketLoadingPlaceholder,
  },
  data() {
    return {
      clientLogos: [
        clientLogo1,
        clientLogo2,
        clientLogo3,
        clientLogo4,
        clientLogo5,
      ],
      stats: {
        10: {
          label: 'Participating Teams',
        },
        '11+': {
          label: 'Venues/Stadiums across India',
        },
        '74+': {
          label: 'Matches',
        },
        '250+': {
          label: 'players across the world',
        },
      },
      useCases: [
        'IPL news & stats websites',
        'Fantasy cricket platforms',
        'Cricket analytics dashboards',
        'Live IPL score apps',
        'Fan engagement & prediction apps',
      ],
      features: [
        {
          content: 'Clean, structured',
          highlight: 'JSON REST APIs',
        },
        {
          highlight: 'Commercial-ready',
          content: 'IPL data licensing',
          highlightFirst: true,
        },
        {
          content: 'Handles high traffic during',
          highlight: '74+ matches',
        },
        {
          content: 'Low-latency',
          highlight: 'IPL live data',
        },
        {
          highlight: 'Developer-friendly',
          content: 'documentation',
          highlightFirst: true,
        },
      ],
      contents: [
        'Full coverage of IPL 2026 matches & teams',
        'Live Match Feeds',
        'Optional access to Historical IPL data (2008 - 2025)',
        'SLA (If required)',
        'Scalable API infrastructure for match peaks',
        'Dedicated technical support',
      ],
      testimonials: [
        {
          name: 'Ascores',
          subtitle: 'Fantasy Gaming Platform',
          logo: ascoresLogo,
          logoBg: '#ffffff',
          hasBorder: true,
          logoImgStyle: {
            width: '100%', height: '100%', objectFit: 'cover',
          },
          quote: '\u201CWorking with Roanuz has been a game changer.'
            + ' Their fast, spot-on cricket data ensures our users'
            + ' never miss a beat and empowers us to deliver live'
            + ' scores and news confidently.\u201D',
        },
        {
          name: 'AD11',
          subtitle: 'Fantasy Sports App',
          logo: ad11Logo,
          logoBg: '#0C393F',
          hasBorder: false,
          logoImgStyle: {
            width: '78%', height: '75%', objectFit: 'contain',
          },
          quote: '\u201COur old API was a constant headache due to'
            + ' slow updates and inaccuracies. Roanuz delivers'
            + ' lightning-fast, reliable data.\u201D',
        },
        {
          name: 'Fantomplay',
          subtitle: 'Fantasy Gaming App',
          logo: fantomplayLogo,
          logoBg: '#ffffff',
          hasBorder: true,
          logoImgStyle: {
            width: '70%', height: '70%', objectFit: 'contain',
          },
          quote: '\u201CWe needed separate innings tracking for our'
            + ' fantasy pools, and Roanuz nailed it. They listened'
            + ' to our vision, delivered smooth, innovative'
            + ' solutions, and their flexibility excites us for'
            + ' future expansion.\u201D',
        },
        {
          name: 'Pagal Fan',
          subtitle: 'Sports Application',
          logo: pagalfanLogo,
          logoBg: '#171717',
          hasBorder: false,
          logoImgStyle: {
            width: '80%', height: 'auto', objectFit: 'contain',
          },
          quote: '\u201CIn our startup\u2019s early days, every'
            + ' decision mattered. Jayakumar guided us to the right'
            + ' package with practical advice, ensuring steady'
            + ' progress without breaking the bank.\u201D',
        },
      ],
      apiColumns: [
        [
          {
            title: 'Tournament Endpoints',
            items: [
              { name: 'Tournament API', link: '/v5/resource/tournament/' },
              { name: 'Tournament Fixtures API', link: '/v5/resource/tournament-fixtures/' },
              { name: 'Tournament Tables API', link: '/v5/resource/tournament-tables/' },
              {
                name: 'Tournament Featured Matches API',
                link: '/v5/resource/tournament-featured-matches/',
              },
              { name: 'Tournament Team API', link: '/v5/resource/tournament-team/' },
            ],
          },
          {
            title: 'Stats Endpoints',
            items: [
              { name: 'Tournament Stats API', link: '/v5/resource/tournament-stats/' },
              {
                name: 'Tournament Player Stats API',
                link: '/v5/resource/tournament-player-stats/',
              },
            ],
          },
        ],
        [
          {
            title: 'Match Endpoints',
            items: [
              { name: 'Match API', link: '/v5/resource/match/' },
              { name: 'Featured Matches API', link: '/v5/resource/featured-matches/' },
              { name: 'Match Over Summary API', link: '/v5/resource/match-ball-by-ball/' },
              { name: 'Match Commentary API', link: '/v5/resource/match-commentary/' },
              { name: 'Match Insights API', link: '/v5/resource/match-insights/' },
            ],
          },
          {
            title: 'Fantasy Endpoints',
            items: [
              { name: 'Fantasy Match Points API', link: '/v5/resource/fantasy-match-points/' },
              { name: 'Fantasy Match Credits API', link: '/v5/resource/fantasy-match-credits/' },
            ],
          },
        ],
        [
          {
            title: 'Match Odds Endpoints',
            items: [
              { name: 'Pre Match Odds API', link: '/v5/resource/pre-match-odds/' },
              { name: 'Live Match Odds API', link: '/v5/resource/live-match-odds/' },
            ],
          },
          {
            title: 'Graph Endpoints',
            items: [
              { name: 'Worm API', link: '/v5/resource/match-worm/' },
              { name: 'Manhattan API', link: '/v5/resource/match-manhattan/' },
              { name: 'Run Rate API', link: '/v5/resource/match-run-rate/' },
              { name: 'Wagon Zone API', link: '/v5/resource/match-wagon-zone/' },
            ],
          },
        ],
      ],
    };
  },
  computed: {
    ...mapState({
      dataLoaded: (state) => state.page.activePageState.status.isLoaded,
      pageState: (state) => state.page.activePageState,
      page: (state) => state.page.activePage,
    }),
  },
  serverPrefetch() {
    return this.fetchData();
  },
  mounted() {
    console.log('this.dataLoaded', this.dataLoaded);
    if (this.dataLoaded) {
      const sameRoute = this.page.route_value === 'rs-campaign-ipl-2026';
      if (!sameRoute) {
        console.log('Refreshing page because page mounted with different route');
        this.refreshPage();
        return;
      }
    }

    if (!this.dataLoaded) {
      this.freshFetch();
    }
    // Handle FAQ links with href="#" to open Intercom
    this.$nextTick(() => {
      this.setupIntercomLinks();
    });
  },
  methods: {
    freshFetch() {
      return this.fetchData();
    },
    getClientLogoPath(n) {
      return this.clientLogos[n - 1];
    },
    fetchData() {
      console.log('Fetching page data for campaign');
      const route = '/rs/campaigns/';
      const routeValue = 'rs-campaign-ipl-2026';
      return this.$store.dispatch('page/fetchActivePage', { route, routeValue });
    },
    refreshPage() {
      this.$store.commit('page/resetActivePage');
      this.freshFetch();
    },
    setupIntercomLinks() {
      const faqSection = document.querySelector('.faqs');
      if (faqSection) {
        const hashLinks = faqSection.querySelectorAll('a[href="#"]');
        hashLinks.forEach((link) => {
          link.addEventListener('click', (e) => {
            e.preventDefault();
            // Trigger Intercom
            if (window.Intercom) {
              window.Intercom('show');
            }
          });
        });
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.ipl-campaign {
  font-family: var(--rz-hero-font);
  background: var(--rs-white-color);

  .container {
    max-width: size(1200);
    margin: 0 auto;
    padding: 0 size(30);

    @media screen and (min-width: $breakpoint-md) {
      padding: 0 size(60);
    }

    @media screen and (min-width: $breakpoint-lg) {
      padding: 0 size(120);
    }
  }

  // Hero Section
  .hero-section {
    background: url('../../assets/campaign/grid1.svg') no-repeat;
    background-size: 100%;
    padding: size(100) 0 size(40) 0;
    position: relative;
    @media screen and (min-width: $breakpoint-lg) {
      padding: size(150) 0 size(40) 0;
    }

    .hero-content {
      display: flex;
      flex-direction: column;
      gap: size(40);

      @media screen and (min-width: $breakpoint-lg) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        gap: size(80);
      }
    }

    .hero-text {
      flex: 1;
      max-width: size(530);
    }

    .hero-subtitle {
      font-size: size(14);
      line-height: size(18);
      color: var(--rs-black-color);
      font-weight: 400;

      @media screen and (min-width: $breakpoint-md) {
        font-size: size(16);
        line-height: size(24);
      }
    }

    .hero-title {
      font-size: size(36);
      font-weight: 800;
      line-height: 1.15;
      color: var(--rz-title-color);
      margin-bottom: size(20);

      @media screen and (min-width: $breakpoint-md) {
        font-size: size(52);
        margin-bottom: size(24);
      }

      @media screen and (min-width: $breakpoint-lg) {
        font-size: size(68);
        line-height: 1.15;
      }
    }

    .hero-description {
      font-size: size(16);
      line-height: size(28);
      color: var(--rs-black-color);
      font-weight: 400;
      margin-bottom: size(30);

      @media screen and (min-width: $breakpoint-md) {
        font-size: size(18);
        line-height: size(30);
        margin-bottom: size(32);
      }
    }

    .cta-buttons {
      display: flex;
      flex-direction: column;
      gap: size(10);
      max-width: size(360);
    }

    .btn-primary {
      background: var(--rs-cta-blue-color);
      color: var(--rs-white-color);
      border: none;
      padding: size(16) size(30);
      font-size: size(14);
      font-weight: 700;
      border-radius: size(5);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: size(10);
      transition: all 0.2s ease;
      font-family: var(--rz-hero-font);

      @media screen and (min-width: $breakpoint-md) {
        font-size: size(15);
        padding: size(20) size(40);
      }

      &:hover {
        background: #0046a0;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 85, 191, 0.2);
      }

      svg {
        width: size(4);
        height: size(8);
      }
    }

    .btn-secondary {
      background: var(--rs-white-color);
      color: var(--rs-cta-blue-color);
      border: 1px solid var(--rs-cta-blue-color);
      padding: size(16) size(30);
      font-size: size(14);
      font-weight: 700;
      border-radius: size(5);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: size(10);
      transition: all 0.2s ease;
      font-family: var(--rz-hero-font);

      @media screen and (min-width: $breakpoint-md) {
        font-size: size(15);
        padding: size(20) size(40);
      }

      &:hover {
        background: rgba(0, 85, 191, 0.05);
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 85, 191, 0.1);
      }

      svg {
        width: size(4);
        height: size(8);
      }
    }

    .hero-image {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .illustration-container {
      width: 100%;
      max-width: size(450);
      position: relative;

      @media screen and (min-width: $breakpoint-md) {
        max-width: size(496);
      }

      @media screen and (min-width: $breakpoint-lg) {
        max-width: size(600);
      }

      img {
        width: 100%;
        height: auto;
        display: block;
      }
    }
    .marquee {
      margin: 0;
      margin-top: size(30);
      width: 50%;
      height: size(60);
      white-space: nowrap;
      overflow: hidden;
      display: none;
      @media screen and (min-width:  $breakpoint-lg) {
        display: flex;
      }
      @media screen and (min-width:  $breakpoint-lg) {
        padding-bottom: size(90);
      }
    }

    .marquee-content {
      width: 100%;
      display: flex;
      animation: marquee 10s linear infinite forwards;
      &:hover {
        animation-play-state: paused;
      }
      img {
        max-height: size(80);
        @media screen and (min-width: $breakpoint-lg) {
          max-height: size(130);
        }
      }
    }

    .list-inline {
      display: flex;
      justify-content: center;
      align-items: center;
      list-style: none;
      margin: 0;
      flex-shrink: 0;
      width: 100%;
      gap: size(20);
    }
    @keyframes marquee {
      0% { transform: translateX(0); }
      100% { transform: translateX(-50%); }
    }
    @media screen and (min-width:  $breakpoint-md) {
      .list-inline {
        width: 50%;
        gap: size(30);
      }
      @keyframes marquee {
        0% { transform: translateX(0); }
        100% { transform: translateX(-300%); }
      }
    }
    @media screen and (min-width:  $breakpoint-lg) {
      @keyframes marquee {
        0% { transform: translateX(0); }
        100% { transform: translateX(-100%); }
      }
    }
  }

  // Client Testimonials Section
  .testimonials-section {
    background: var(--rs-white-color);
    padding: 0 0 size(40);
    @media screen and (min-width: $breakpoint-md) {
      padding: 0 0 size(60);
    }
    @media screen and (min-width: $breakpoint-md) {
      padding: 0 0 size(80);
    }

    .section-title {
      font-family: 'Outfit', sans-serif;
      font-size: size(28);
      font-weight: 700;
      line-height: size(38);
      color: var(--rs-black-color);
      margin-bottom: size(24);

      @media screen and (min-width: $breakpoint-md) {
        font-size: size(36);
        line-height: size(48);
        margin-bottom: size(32);
      }

      @media screen and (min-width: $breakpoint-lg) {
        font-size: size(40);
        line-height: size(54);
      }

      .highlight-pink {
        color: #dd4c80;
      }
    }

    .testimonials-grid {
      display: flex;
      flex-direction: column;
      gap: size(16);

      @media screen and (min-width: $breakpoint-md) {
        flex-direction: row;
        flex-wrap: wrap;
      }

      @media screen and (min-width: $breakpoint-lg) {
        flex-wrap: nowrap;
      }
    }

    .testimonial-card {
      background: var(--rs-white-color);
      border: 1px solid #c0c0c0;
      border-radius: size(18);
      padding: size(24);
      display: flex;
      flex-direction: column;
      gap: size(20);
      flex: 1;
      min-width: 0;
      transition: box-shadow 0.3s ease, transform 0.3s ease;

      &:hover {
        box-shadow: 0 8px 30px rgba(141, 151, 158, 0.25);
        transform: translateY(-2px);
      }

      @media screen and (min-width: $breakpoint-md) {
        flex: 0 0 calc(50% - #{size(8)});
      }

      @media screen and (min-width: $breakpoint-lg) {
        flex: 1;
      }
    }

    .testimonial-header {
      display: flex;
      gap: size(12);
      align-items: center;
    }

    .logo-circle {
      width: size(40);
      height: size(40);
      border-radius: 50%;
      overflow: hidden;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;

      &.bordered {
        border: 1px solid #ededed;
      }

      img {
        display: block;
      }
    }

    .client-info {
      display: flex;
      flex-wrap: wrap;
      gap: size(4);
      align-items: center;
      padding-top: size(4);
      width: size(160);
    }

    .client-name {
      font-family: 'Outfit', sans-serif;
      font-size: size(16);
      font-weight: 600;
      line-height: size(17);
      color: var(--rs-black-color);
    }

    .client-subtitle {
      font-size: size(12);
      font-weight: 400;
      line-height: normal;
      color: var(--rs-black-color);
      width: size(160);
    }

    .testimonial-divider {
      width: 100%;
      height: 1px;
      background: #ececec;
    }

    .testimonial-quote {
      font-size: size(16);
      font-weight: 400;
      line-height: size(24);
      color: var(--rs-black-color);
    }
  }

  // Tournament Overview Section
  .tournament-overview {
    background: var(--rs-black-color);
    color: var(--rs-white-color);
    padding: size(80) 0;

    @media screen and (min-width: $breakpoint-lg) {
      padding: size(120) 0;
    }

    .overview-content {
      display: flex;
      flex-direction: column;
      gap: size(50);

      @media screen and (min-width: $breakpoint-lg) {
        flex-direction: row;
        align-items: flex-start;
        gap: size(100);
      }
    }

    .overview-text {
      flex: 1;
      max-width: 100%;

      @media screen and (min-width: $breakpoint-lg) {
        max-width: size(670);
      }
    }

    .section-title {
      font-size: size(44);
      font-weight: 800;
      line-height: 1.21;
      margin-bottom: size(14);
      color: var(--rs-white-color);

      @media screen and (min-width: $breakpoint-md) {
        font-size: size(56);
      }

      @media screen and (min-width: $breakpoint-lg) {
        font-size: size(68);
        line-height: 1.21;
      }

      .highlight-yellow {
        color: #f5ff78;
      }
    }

    .section-description {
      font-size: size(18);
      line-height: 1.45;
      margin-bottom: size(40);
      opacity: 1;

      @media screen and (min-width: $breakpoint-md) {
        font-size: size(20);
      }

      @media screen and (min-width: $breakpoint-lg) {
        margin-bottom: size(60);
        font-size: size(22);
        line-height: 1.45;
      }
    }

    .stats-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: size(15);

      @media screen and (min-width: $breakpoint-md) {
        grid-template-columns: repeat(2, 1fr);
      }

      @media screen and (min-width: $breakpoint-lg) {
        grid-template-columns: auto auto auto auto;
        gap: size(15);
      }
    }

    .stat-item {
      background: #1c1c1c;
      padding: size(24);
      border-radius: size(18);
      display: flex;
      flex-direction: column;
      gap: size(4);

      @media screen and (min-width: $breakpoint-md) {
        min-width: size(130);
      }

      @media screen and (min-width: $breakpoint-lg) {
        min-width: size(180);
      }

      &.stat-wide {
        @media screen and (min-width: $breakpoint-md) {
          min-width: size(200);
        }
        @media screen and (min-width: $breakpoint-lg) {
          min-width: size(230);
        }
      }
    }

    .stat-number {
      font-size: size(48);
      font-weight: 500;
      color: var(--rs-pink-color);
      line-height: 1;
      margin-bottom: size(4);

      @media screen and (min-width: $breakpoint-md) {
        font-size: size(56);
      }

      @media screen and (min-width: $breakpoint-lg) {
        font-size: size(60);
      }
    }

    .stat-label {
      font-size: size(14);
      color: var(--rs-white-color);
      line-height: 1.73;
      font-weight: 400;

      @media screen and (min-width: $breakpoint-md) {
        font-size: size(15);
        line-height: 1.73;
      }
    }

    .overview-image {
      flex: 0 0 auto;
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;

      @media screen and (min-width: $breakpoint-lg) {
        min-width: size(400);
      }

      img {
        width: 100%;
        max-width: size(300);
        height: auto;
        display: block;

        @media screen and (min-width: $breakpoint-md) {
          max-width: size(350);
        }

        @media screen and (min-width: $breakpoint-lg) {
          max-width: size(370);
        }

        @media screen and (min-width: size(1300)) {
          max-width: size(472);
        }
      }
    }
  }

  // Build Section
  .build-section {
    background-color: var(--rs-white-color);
    background: url('../../assets/campaign/grid1.svg') no-repeat;
    background-size: 100%;
    padding: size(60) 0;
    position: relative;
    overflow: hidden;

    @media screen and (min-width: $breakpoint-lg) {
      padding: size(120) 0;
    }

    .build-content {
      display: flex;
      flex-direction: column;
      gap: size(40);
      position: relative;
      z-index: 1;

      @media screen and (min-width: $breakpoint-lg) {
        flex-direction: row;
        align-items: center;
        gap: size(80);
      }
    }

    .build-text {
      max-width: 100%;

      @media screen and (min-width: $breakpoint-lg) {
        max-width: size(760);
      }
    }

    .section-title {
      font-size: size(36);
      font-weight: 800;
      line-height: size(48);
      color: var(--rs-black-color);
      margin-bottom: size(6);
      text-transform: capitalize;
      padding-right: 0;

      @media screen and (min-width: $breakpoint-md) {
        font-size: size(52);
        line-height: size(64);
      }

      @media screen and (min-width: $breakpoint-lg) {
        font-size: size(68);
        line-height: size(80);
      }

      .highlight-green {
        color: #00a656;
      }
    }

    .section-subtitle {
      font-size: size(16);
      color: var(--rs-black-color);
      margin-bottom: size(30);
      font-weight: 500;

      @media screen and (min-width: $breakpoint-md) {
        margin-bottom: size(40);
        font-size: size(18);
      }
    }

    .features-list {
      display: flex;
      flex-direction: column;
      gap: size(12);
    }

    .feature {
      display: flex;
      align-items: center;
      gap: size(6);
      font-size: size(16);
      color: var(--rs-black-color);
      font-family: 'Outfit', sans-serif;
      font-weight: 400;
      line-height: size(31);

      @media screen and (min-width: $breakpoint-md) {
        font-size: size(18);
      }
      svg {
        flex-shrink: 0;
        width: size(13);
        height: size(13);
      }
    }

    .image-container {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;

      img {
        width: 100%;
        max-width: size(300);
        height: auto;
        display: block;


        @media screen and (min-width: $breakpoint-lg) {
          max-width: size(450);
        }
      }
    }
  }

  // Why Section
  .why-section {
    background: #0055bf;
    color: var(--rs-white-color);
    padding: size(60) 0 size(370);
    position: relative;
    @media screen and (min-width: $breakpoint-md) {
      padding: size(60) 0;
    }

    @media screen and (min-width: $breakpoint-lg) {
      padding: size(120) 0;
    }

    .why-content {
      display: flex;
      flex-direction: column;
      gap: size(40);
      align-items: flex-start;
      position: relative;

      @media screen and (min-width: $breakpoint-lg) {
        flex-direction: row;
        gap: size(120);
        align-items: center;
      }
    }

    .why-text {
      flex-shrink: 0;

      @media screen and (min-width: $breakpoint-lg) {
        width: size(460);
      }
    }

    .section-title {
      font-size: size(36);
      font-weight: 800;
      line-height: size(48);
      color: var(--rs-white-color);
      padding-right: 0;

      @media screen and (min-width: $breakpoint-md) {
        font-size: size(52);
        line-height: size(68);
      }

      @media screen and (min-width: $breakpoint-lg) {
        font-size: size(68);
        line-height: size(82);
      }

      .highlight-yellow {
        color: #f5ff78;
      }
    }

    .why-features {
      display: flex;
      flex-direction: column;
      gap: size(12);
      flex-shrink: 0;

      @media screen and (min-width: $breakpoint-lg) {
        width: size(353);
      }
    }

    .why-feature-item {
      display: flex;
      align-items: center;
      gap: size(6);
      font-size: size(16);
      font-family: 'Outfit', sans-serif;
      font-weight: 400;
      color: var(--rs-white-color);
      line-height: size(29);

      @media screen and (min-width: $breakpoint-md) {
        font-size: size(18);
      }

      svg {
        flex-shrink: 0;
        width: size(13);
        height: size(13);
      }

      strong {
        font-weight: 600;
      }
    }

    .why-image {
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      bottom: size(-370);

      @media screen and (min-width: $breakpoint-md) {
        right: size(-60);
        bottom: size(-60);
      }

      @media screen and (min-width: $breakpoint-lg) {
        position: absolute;
        right: size(-120);
        bottom: size(-120);
      }

      img {
        width: size(240);
        height: auto;
        display: block;
        @media screen and (min-width: $breakpoint-lg) {
          width: size(240);
        }

        @media screen and (min-width: 1300px) {
          width: size(316);
        }
      }
    }
  }

  // Get Section
  .get-section {
    background: var(--rs-white-color);
    padding: size(60) 0;
    position: relative;

    @media screen and (min-width: $breakpoint-lg) {
      padding: size(120) 0;
    }

    .get-content {
      display: flex;
      flex-direction: column;
      gap: size(40);

      @media screen and (min-width: $breakpoint-lg) {
        flex-direction: row;
        align-items: flex-start;
        gap: size(50);
      }
    }

    .get-text {
      flex-shrink: 0;

      @media screen and (min-width: $breakpoint-lg) {
        width: size(720);
      }
    }

    .section-title {
      font-size: size(36);
      font-weight: 800;
      line-height: size(48);
      color: var(--rs-black-color);
      margin-bottom: size(40);

      @media screen and (min-width: $breakpoint-md) {
        font-size: size(52);
        line-height: size(68);
      }

      @media screen and (min-width: $breakpoint-lg) {
        font-size: size(68);
        line-height: size(78);
      }
    }

    .api-features {
      display: flex;
      flex-wrap: wrap;
      gap: size(12);
      align-items: center;
    }

    .api-feature {
      display: inline-flex;
      align-items: center;
      gap: size(6);
      font-size: size(16);
      color: var(--rs-black-color);
      font-weight: 400;
      background: #f4fbf8;
      padding: size(12);
      border-radius: size(9);
      line-height: size(28);

      svg {
        flex-shrink: 0;
        width: size(22);
        height: size(22);
      }
    }

    .image-container {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;
      @media screen and (min-width: $breakpoint-lg) {
        min-width: size(350);
      }

      @media screen and (min-width: size(1300)) {
        min-width: size(430);
      }

      img {
        width: 100%;
        max-width: size(350);
        height: auto;
        display: block;

        @media screen and (min-width: $breakpoint-md) {
          max-width: size(350);
        }
        @media screen and (min-width: size(1300)) {
          max-width: size(420);
        }
      }
    }
  }

  // Access APIs Section
  .access-apis-section {
    background: #111;
    color: var(--rs-white-color);
    padding: size(60) 0;
    position: relative;
    overflow: hidden;

    @media screen and (min-width: $breakpoint-lg) {
      padding: size(120) 0;
    }

    .apis-content {
      position: relative;
    }

    .section-title {
      font-size: size(36);
      font-weight: 800;
      line-height: size(48);
      margin-bottom: size(60);
      color: var(--rs-white-color);

      @media screen and (min-width: $breakpoint-md) {
        font-size: size(52);
        line-height: size(68);
      }

      @media screen and (min-width: $breakpoint-lg) {
        font-size: size(68);
        line-height: size(78);
        max-width: size(834);
      }

      .highlight-yellow {
        color: #f5ff78;
      }
    }

    .api-categories {
      display: grid;
      grid-template-columns: 1fr;
      gap: size(36);

      @media screen and (min-width: $breakpoint-md) {
        grid-template-columns: repeat(2, 1fr);
        gap: size(40);
      }

      @media screen and (min-width: $breakpoint-lg) {
        grid-template-columns: repeat(3, 1fr);
        gap: size(64);
      }
    }

    .api-column {
      display: flex;
      flex-direction: column;
      gap: size(40);
    }

    .api-category {
      display: flex;
      flex-direction: column;
      gap: size(12);

      h3 {
        font-size: size(14);
        font-weight: 700;
        color: var(--rs-white-color);
        font-family: var(--rz-hero-font);
        letter-spacing: 0.32px;
        text-transform: capitalize;

        @media screen and (min-width: $breakpoint-md) {
          font-size: size(16);
        }
      }

      ul {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 0;
      }

      li {
        font-size: size(14);
        font-family: var(--rz-hero-font);
        font-weight: 400;
        letter-spacing: 0.32px;
        line-height: 1.26;
        padding: size(6) 0;
        text-transform: capitalize;

        @media screen and (min-width: $breakpoint-md) {
          font-size: size(16);
        }

        a {
          color: var(--rs-white-color);
          text-decoration: none;
          transition: opacity 0.2s ease;

          &:hover {
            opacity: 0.7;
          }
        }
      }
    }

    .api-illustration {
      position: absolute;
      width: size(230);
      height: size(215);
      display: none;
      pointer-events: none;

      @media screen and (min-width: $breakpoint-lg) {
        display: block;
        top: size(-20);
        right: size(0);
      }
      @media screen and (min-width: size(1300)) {
        top: size(-40);
        right: size(120);
      }

      img {
        width: 100%;
        height: auto;
        display: block;
      }
    }
  }

  .final-cta-container {
    position: relative;
  }

  // Final CTA Section 1
  .final-cta-section-1 {
    background-image: url('../../assets/grid-2.svg');
    background-repeat: no-repeat;
    background-size: 100%;
    background-color: #0055bf;
    color: var(--rs-white-color);
    padding: size(60) 0 size(120);
    position: relative;
    overflow: hidden;
    @media screen and (min-width: $breakpoint-md) {
      padding: size(60) 0;
    }

    @media screen and (min-width: $breakpoint-lg) {
      padding: size(120) 0;
    }

    .cta-subtitle {
      font-size: size(16);
      margin-bottom: size(20);
      font-weight: 400;
      font-family: var(--rz-hero-font);

      @media screen and (min-width: $breakpoint-md) {
        font-size: size(20);
      }

      @media screen and (min-width: $breakpoint-lg) {
        font-size: size(22);
      }
    }

    .cta-title {
      font-size: size(36);
      font-weight: 800;
      line-height: 1.25;
      color: var(--rs-white-color);
      max-width: size(725);

      @media screen and (min-width: $breakpoint-md) {
        font-size: size(52);
      }

      @media screen and (min-width: $breakpoint-lg) {
        font-size: size(68);
        line-height: 1.25;
      }

      .highlight-yellow {
        color: #f5ff78;
      }
    }
  }

  // Final CTA Section 2
  .final-cta-section-2 {
    background: #F5FF78;;
    padding: 0;
    overflow: hidden;

    .btn-cta-primary {
      background: #f5ff78;
      color: #0055bf;
      border: none;
      padding: size(30) 0;
      font-size: size(14);
      font-weight: 700;
      border-radius: size(6);
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: size(10);
      transition: all 0.2s ease;
      font-family: var(--rz-hero-font);

      @media screen and (min-width: $breakpoint-md) {
        font-size: size(16);
        padding: size(40) 0;
      }

      @media screen and (min-width: $breakpoint-lg) {
        font-size: size(28);
        font-style: normal;
        font-weight: 500;
        padding: size(40) size(0);
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(245, 255, 120, 0.3);
      }

      svg {
        width: size(10);
        height: size(10);

        @media screen and (min-width: $breakpoint-md) {
          width: size(12);
          height: size(12);
        }
      }
    }

    img {
      width: 100%;
      max-width: size(230);
      height: auto;
      display: block;
      position: absolute;
      bottom: size(74);
      right: size(10);

      @media screen and (min-width: $breakpoint-md) {
        max-width: size(320);
        right: size(60);
        bottom: 0;
      }

      @media screen and (min-width: $breakpoint-lg) {
        max-width: size(480);
        right: size(120);
      }
      @media screen and (min-width: size(1300)) {
        max-width: size(580);
      }
    }
  }
}

</style>

<style lang="scss">
</style>
